import { debugLog } from '@schibsted-nmp/advertising-shared';

import type { Pbjs } from '../Pbjs.js';
import type { PrebidRequester } from './PrebidRequester.js';
import { Targeting } from './Targeting.js';
import type { CustomSettings } from './CustomSettings.js';
import type { ConsentManagement } from './ConsentManagement.js';

export namespace BidderSpecific {
  export namespace Settings {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    export function* get<TMap extends CustomSettings.Map>(
      bidderCodeToCustomSettingsMap: TMap
    ) {
      for (const [
        bidderCode,
        { revenueFactor }
      ] of bidderCodeToCustomSettingsMap) {
        if (!revenueFactor) continue;

        yield [
          bidderCode,
          {
            bidCpmAdjustment(bidCpm, bidResponse, _bidRequest) {
              return bidResponse?.netRevenue ? bidCpm : bidCpm * revenueFactor;
            }
          }
        ] as const satisfies readonly [
          bidderCode: string,
          bidderSettings: Pbjs.BidderSettings.Value
        ];
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    export function apply<TMap extends CustomSettings.Map>(
      pbjs: PrebidRequester['pbjs'],
      bidderCodeToCustomSettingsMap: TMap
    ) {
      pbjs.que.push(() => {
        const bidderSettings = (pbjs.bidderSettings ??= {});

        for (const [bidderCode, bidderSpecificSettings] of get(
          bidderCodeToCustomSettingsMap
        )) {
          bidderSettings[bidderCode] = bidderSpecificSettings;
        }

        const standard = (bidderSettings.standard ??= {});
        standard.adserverTargeting ??= Array.from(Targeting.Mappers.get(pbjs));
        standard.storageAllowed = true;
      });
    }
  }

  export namespace Configs {
    export function* get(
      customSettingsMap: CustomSettings.Map,
      customConsentVendorsMap: ConsentManagement.CustomVendors.Map
    ) {
      yield* get.customOverrides(customSettingsMap);
      yield* get.schain(customConsentVendorsMap);
    }

    export namespace get {
      /** Apply custom config overrides per bidder. */
      export function* customOverrides(customSettingsMap: CustomSettings.Map) {
        for (const [bidderCode, { configOverrides }] of customSettingsMap) {
          if (!configOverrides) continue;

          try {
            const config = JSON.parse(configOverrides) as unknown;

            if (typeof config !== 'object' || config === null) {
              throw new Error('Config overrides is not an object');
            }

            yield {
              bidders: [bidderCode],
              config
            } as const satisfies Pbjs.BidderConfig;
          } catch (error) {
            debugLog(
              'Failed parsing bidder-specific configOverrides JSON',
              { bidderCode, configOverrides },
              error
            );
          }
        }
      }

      /** Apply schain config per bidder. */
      export function* schain(
        customConsentVendorsMap: ConsentManagement.CustomVendors.Map
      ) {
        for (const [bidderCode, { schainNodes }] of customConsentVendorsMap) {
          yield {
            bidders: [bidderCode],
            config: {
              schain: {
                validation: 'relaxed',
                config: {
                  ver: '1.0',
                  nodes: schainNodes,
                  complete: 1
                }
              }
            }
          } as const satisfies Pbjs.BidderConfig;
        }
      }
    }

    export function apply(
      pbjs: PrebidRequester['pbjs'],
      customSettingsMap: CustomSettings.Map,
      customConsentVendorsMap: ConsentManagement.CustomVendors.Map
    ) {
      pbjs.que.push(() => {
        for (const bidderConfig of get(
          customSettingsMap,
          customConsentVendorsMap
        )) {
          try {
            pbjs.setBidderConfig(bidderConfig, true);
          } catch (error) {
            debugLog('Failed to set bidder config', { bidderConfig, error });
          }
        }
      });
    }
  }
}
